import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Search,
  Trash2,
  Eye,
  Calendar,
  Mail,
  Phone,
  Loader2,
  XCircle,
  Shield,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import type { Applicant } from "@/types/applicant";

interface RejectedApplicantsListProps {
  applicants: Applicant[];
  onUpdate: () => void;
}

const RejectedApplicantsList = ({ applicants, onUpdate }: RejectedApplicantsListProps) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // Filter only rejected applicants
  const rejectedApplicants = applicants.filter(a => a.current_stage === "rejected");

  const filteredApplicants = rejectedApplicants.filter((applicant) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      applicant.first_name.toLowerCase().includes(searchLower) ||
      applicant.last_name.toLowerCase().includes(searchLower) ||
      applicant.email.toLowerCase().includes(searchLower) ||
      applicant.desired_position?.toLowerCase().includes(searchLower)
    );
  });

  const handlePermanentDelete = async (applicant: Applicant) => {
    setDeletingId(applicant.id);
    try {
      // 1. Delete all documents from storage
      const { data: documents } = await supabase
        .from("applicant_documents")
        .select("file_path")
        .eq("applicant_id", applicant.id);

      if (documents && documents.length > 0) {
        const filePaths = documents.map(d => d.file_path);
        await supabase.storage
          .from("applicant-documents")
          .remove(filePaths);
      }

      // 2. Delete document records
      await supabase
        .from("applicant_documents")
        .delete()
        .eq("applicant_id", applicant.id);

      // 3. Delete stage history
      await supabase
        .from("applicant_stage_history")
        .delete()
        .eq("applicant_id", applicant.id);

      // 4. Delete interview invitations
      await supabase
        .from("applicant_interview_invitations")
        .delete()
        .eq("applicant_id", applicant.id);

      // 5. Delete rejection emails records
      await supabase
        .from("applicant_rejection_emails")
        .delete()
        .eq("applicant_id", applicant.id);

      // 6. Delete offer emails records
      await supabase
        .from("applicant_offer_emails")
        .delete()
        .eq("applicant_id", applicant.id);

      // 7. Finally delete the applicant
      const { error } = await supabase
        .from("applicants")
        .delete()
        .eq("id", applicant.id);

      if (error) throw error;

      toast.success("Bewerber und alle Daten DSGVO-konform gelöscht");
      onUpdate();
    } catch (error) {
      console.error("Error deleting applicant:", error);
      toast.error("Fehler beim Löschen des Bewerbers");
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async (applicant: Applicant) => {
    setRestoringId(applicant.id);
    try {
      const { error } = await supabase
        .from("applicants")
        .update({ current_stage: "new" })
        .eq("id", applicant.id);

      if (error) throw error;

      // Add stage history
      await supabase.from("applicant_stage_history").insert({
        applicant_id: applicant.id,
        from_stage: "rejected",
        to_stage: "new",
        changed_by: profile?.full_name || "System",
        notes: "Bewerber wiederhergestellt",
      });

      toast.success("Bewerber in Pipeline wiederhergestellt");
      onUpdate();
    } catch (error) {
      console.error("Error restoring applicant:", error);
      toast.error("Fehler beim Wiederherstellen");
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            Abgesagte Bewerber
            <Badge variant="secondary">{rejectedApplicants.length}</Badge>
          </CardTitle>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Bewerber suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredApplicants.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <XCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Keine abgesagten Bewerber vorhanden</p>
          </div>
        ) : (
          <>
            <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-900 mb-4">
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    DSGVO-Hinweis
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Abgesagte Bewerber können hier endgültig gelöscht werden. Dabei werden alle 
                    personenbezogenen Daten und Dokumente unwiderruflich entfernt.
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Position</TableHead>
                    <TableHead className="hidden md:table-cell">Kontakt</TableHead>
                    <TableHead className="hidden lg:table-cell">Absagedatum</TableHead>
                    <TableHead className="hidden lg:table-cell">Grund</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredApplicants.map((applicant) => (
                    <TableRow key={applicant.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {applicant.first_name} {applicant.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground sm:hidden">
                            {applicant.desired_position || "-"}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {applicant.desired_position || "-"}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="truncate max-w-[150px]">{applicant.email}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            <span>{applicant.phone}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          {new Date(applicant.updated_at).toLocaleDateString("de-DE")}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-sm text-muted-foreground truncate max-w-[150px] block">
                          {applicant.rejection_reason || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/applicants/${applicant.id}`)}
                            title="Ansehen"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRestore(applicant)}
                            disabled={restoringId === applicant.id}
                            title="Wiederherstellen"
                          >
                            {restoringId === applicant.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </Button>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                title="Endgültig löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                  <AlertTriangle className="h-5 w-5 text-destructive" />
                                  Endgültig löschen?
                                </AlertDialogTitle>
                                <AlertDialogDescription className="space-y-2">
                                  <p>
                                    Möchten Sie <strong>{applicant.first_name} {applicant.last_name}</strong> 
                                    {" "}und alle zugehörigen Daten endgültig löschen?
                                  </p>
                                  <div className="bg-destructive/10 p-3 rounded-lg border border-destructive/20 mt-2">
                                    <p className="text-sm font-medium text-destructive">
                                      Folgende Daten werden unwiderruflich gelöscht:
                                    </p>
                                    <ul className="text-sm text-muted-foreground mt-1 list-disc list-inside">
                                      <li>Alle Bewerbungsunterlagen (Lebenslauf, Zeugnisse, etc.)</li>
                                      <li>Kontaktdaten und persönliche Informationen</li>
                                      <li>Bewerbungshistorie und Notizen</li>
                                      <li>Interview-Einladungen und Korrespondenz</li>
                                    </ul>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    Diese Aktion entspricht den DSGVO-Anforderungen und kann nicht 
                                    rückgängig gemacht werden.
                                  </p>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handlePermanentDelete(applicant)}
                                  className="bg-destructive hover:bg-destructive/90"
                                  disabled={deletingId === applicant.id}
                                >
                                  {deletingId === applicant.id ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4 mr-2" />
                                  )}
                                  Endgültig löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RejectedApplicantsList;
